import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../store/slices/authSlice';
import { addNotification } from '../store/slices/notificationSlice';
import { subscribeToNotifications, unsubscribeFromNotifications } from '../services/socket';
import { changePassword } from '../services/api';
import { setAlert } from '../store/slices/alertSlice';

const Header = ({ sidebarCollapsed = false, onToggleSidebar }) => {
  const notifications = useSelector((state) => state.notifications.notifications);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  useEffect(() => {
    const handleNewNotification = (notification) => {
      dispatch(addNotification(notification));
    };

    subscribeToNotifications(handleNewNotification);

    return () => {
      unsubscribeFromNotifications();
    };
  }, [dispatch]);

  const handleLogout = () => {
    setShowUserMenu(false);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    dispatch(logout());
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const submitPasswordChange = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      dispatch(setAlert({ type: 'danger', message: 'Enter all password fields.' }));
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      dispatch(setAlert({ type: 'danger', message: 'New password and confirm password do not match.' }));
      return;
    }

    try {
      await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordModal(false);
      dispatch(setAlert({ type: 'success', message: 'Password changed successfully.' }));
    } catch (error) {
      dispatch(setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Password change failed.',
      }));
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowPassword({ currentPassword: false, newPassword: false, confirmPassword: false });
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const passwordStrength = passwordData.newPassword.length >= 8
    ? 'Strong'
    : passwordData.newPassword.length >= 4
      ? 'Fair'
      : 'Weak';

  return (
    <header className={`app-header ${sidebarCollapsed ? 'collapsed' : ''}`}>
      <div className="app-header-title">
        <button
          type="button"
          className="app-sidebar-toggle"
          onClick={onToggleSidebar}
          title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
          aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
        >
          <i className="bi bi-list" aria-hidden="true"></i>
        </button>
        <div className="welcome-copy">
          <span>{getGreeting()}, {user?.name || user?.username || 'Guest'}</span>
          <small>Welcome back to Incident Reporting System</small>
        </div>
      </div>

      <div className="app-header-actions">
        <div className="dropdown user-dropdown">
          <button
            className="header-icon-btn"
            type="button"
            id="notificationDropdown"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            title="Notifications"
          >
            <i className="bi bi-bell" aria-hidden="true"></i>
            {notifications.length > 0 && (
              <span className="notification-count">{notifications.length}</span>
            )}
          </button>
          <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="notificationDropdown">
            {notifications.length > 0 ? (
              notifications.map((notif, index) => (
                <li key={index} className="dropdown-item">
                  {notif.message}
                </li>
              ))
            ) : (
              <li className="dropdown-item text-muted">No new notifications</li>
            )}
          </ul>
        </div>

        <div className="dropdown">
          <button
            type="button"
            className="header-avatar-btn"
            id="userMenuDropdown"
            aria-expanded={showUserMenu}
            title="User menu"
            onClick={() => setShowUserMenu((value) => !value)}
          >
            {(user?.name || user?.username || 'G').charAt(0).toUpperCase()}
          </button>
          <ul
            className={`dropdown-menu dropdown-menu-end user-menu ${showUserMenu ? 'show' : ''}`}
            aria-labelledby="userMenuDropdown"
          >
            <li className="user-menu-header">
              <strong>{user?.name || user?.username || 'Guest'}</strong>
              <span>{user?.role || 'User'}</span>
            </li>
            <li><hr className="dropdown-divider" /></li>
            <li>
              <button
                type="button"
                className="dropdown-item"
                onClick={() => {
                  setShowUserMenu(false);
                  setShowPasswordModal(true);
                }}
              >
                <i className="bi bi-key" aria-hidden="true"></i>
                Change Password
              </button>
            </li>
            <li>
              <button type="button" className="dropdown-item text-danger" onClick={handleLogout}>
                <i className="bi bi-box-arrow-right" aria-hidden="true"></i>
                Logout
              </button>
            </li>
          </ul>
        </div>
      </div>

      {showPasswordModal && createPortal(
        <>
        <div className="modal fade show d-block password-modal" tabIndex="-1" style={{ zIndex: 2060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title">Change Password</h5>
                  <p className="password-modal-subtitle">Update your account password securely.</p>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closePasswordModal}
                ></button>
              </div>
              <div className="modal-body">
                {[
                  ['currentPassword', 'Current Password', 'bi-lock'],
                  ['newPassword', 'New Password', 'bi-shield-lock'],
                  ['confirmPassword', 'Confirm Password', 'bi-check2-circle'],
                ].map(([field, label, icon]) => (
                  <label className="password-field" key={field}>
                    <span>{label}</span>
                    <div className="password-input-wrap">
                      <i className={`bi ${icon}`} aria-hidden="true"></i>
                      <input
                        type={showPassword[field] ? 'text' : 'password'}
                        name={field}
                        value={passwordData[field]}
                        onChange={handlePasswordChange}
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility(field)}
                        title={showPassword[field] ? 'Hide password' : 'Show password'}
                        aria-label={showPassword[field] ? 'Hide password' : 'Show password'}
                      >
                        <i className={`bi ${showPassword[field] ? 'bi-eye-slash' : 'bi-eye'}`} aria-hidden="true"></i>
                      </button>
                    </div>
                  </label>
                ))}
                <div className="password-hint">
                  <span>Strength: {passwordStrength}</span>
                  <span>Use at least 8 characters.</span>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="password-secondary-btn"
                  onClick={closePasswordModal}
                >
                  Cancel
                </button>
                <button type="button" className="password-primary-btn" onClick={submitPasswordChange}>
                  <i className="bi bi-check2" aria-hidden="true"></i>
                  Update Password
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" style={{ zIndex: 2055 }}></div>
        </>,
        document.body
      )}
    </header>
  );
};

export default Header;
